import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Mail, MessageCircle, MailWarning, Instagram, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ShareDialog = ({ open, onOpenChange, url, title, storyUrl }) => {
  const { user } = useAuth();
  const { t } = useLang();
  const [igBusy, setIgBusy] = useState(false);
  const text = encodeURIComponent(`${title} 🎁 ${url}`);
  const encoded = encodeURIComponent(url);
  const showVerifyNotice = user && user.auth_provider === "email" && !user.email_verified;

  const shareInstagram = async () => {
    setIgBusy(true);
    try {
      const res = await fetch(storyUrl);
      if (!res.ok) throw new Error("og failed");
      const blob = await res.blob();
      const file = new File([blob], "hediyeniiste-story.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "hediyeniiste-story.png";
        a.click();
        URL.revokeObjectURL(a.href);
      }
      toast.success(t("ig_ready"));
    } catch (err) {
      if (err.name !== "AbortError") toast.error(t("ig_fail"));
    } finally { setIgBusy(false); }
  };

  const resendVerification = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("verification_sent"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("link_copied"));
    } catch {
      toast.error(t("copy_fail"));
    }
  };

  const links = [
    { label: "WhatsApp", href: `https://wa.me/?text=${text}`, icon: MessageCircle, color: "bg-[#25D366]" },
    { label: "X", href: `https://twitter.com/intent/tweet?text=${text}`, icon: null, color: "bg-foreground", char: "𝕏" },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`, icon: null, color: "bg-[#1877F2]", char: "f" },
    { label: t("email_label"), href: `mailto:?subject=${encodeURIComponent(title)}&body=${text}`, icon: Mail, color: "bg-muted-foreground" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">{t("share")}</DialogTitle></DialogHeader>
        {showVerifyNotice && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex items-start gap-2.5" data-testid="share-verify-notice">
            <MailWarning size={16} className="text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">{t("verify_share_notice")}</p>
              <Button size="sm" variant="outline" className="rounded-full text-xs mt-2 h-7" onClick={resendVerification} data-testid="share-resend-verification-btn">
                {t("send_verification")}
              </Button>
            </div>
          </div>
        )}
        <div className="flex justify-center py-4">
          <div className="bg-white p-4 rounded-2xl border border-border" data-testid="share-qr-code">
            <QRCodeSVG value={url} size={160} fgColor="#1A1A1A" />
          </div>
        </div>
        <div className="flex items-center gap-2 bg-secondary rounded-xl p-3 min-w-0">
          <p className="text-xs truncate flex-1 min-w-0 text-muted-foreground" data-testid="share-url-text">{url}</p>
          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={copy} data-testid="share-copy-btn">
            <Copy size={13} className="mr-1" /> {t("copy_btn")}
          </Button>
        </div>
        <div className="grid grid-cols-4 gap-3 pt-2">
          {links.map(({ label, href, icon: Icon, color, char }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
              data-testid={`share-${label.toLowerCase().replace("-", "")}-btn`}
              className="flex flex-col items-center gap-1.5 group">
              <div className={`w-11 h-11 rounded-full ${color} text-white flex items-center justify-center group-active:scale-90 transition-transform font-bold`}>
                {Icon ? <Icon size={18} /> : char}
              </div>
              <span className="text-[10px] text-muted-foreground">{label}</span>
            </a>
          ))}
        </div>
        {storyUrl && (
          <div className="pt-1">
            <Button onClick={shareInstagram} disabled={igBusy} data-testid="share-instagram-btn"
              className="w-full rounded-full h-11 font-semibold text-white border-0 bg-[linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)] hover:opacity-90 active:scale-95 transition-all">
              {igBusy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Instagram size={16} className="mr-2" />}
              {t("ig_btn")}
            </Button>
            <p className="text-[10px] text-muted-foreground text-center mt-1.5">{t("ig_hint")}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
